/**
 * Spec Phase 9 Slice 9D — Stage 3 / Stage 5 banner integration.
 *
 * Surfaces sent revision proposals (target_kind matches the
 * page) at the top of the Stage 3 (claims) and Stage 5
 * (drawings) pages. Each row exposes the proposal text +
 * rationale + three buttons:
 *
 *   - Mark consumed → POST /spec/revision-proposals/{id}/mark-consumed
 *   - Cancel proposal → PATCH status='cancelled'
 *   - Edit (placeholder) → just toasts; actual edit happens
 *     through the existing Stage 3/5 flows. The banner is
 *     informational; the drafter chooses how to make the
 *     change.
 *
 * Self-contained: re-fetches on mount + after each transition
 * so the row count stays accurate without subscribing to the
 * full spec store. Lives outside the spec module's right rail
 * since Stage 3 / Stage 5 are separate pages with their own
 * layouts.
 *
 * Architectural rule: this component does NOT mutate
 * claims-side or drawing-side rows. It only writes to
 * spec_revision_proposals via the spec API.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { Check, Flag, Loader2, X } from 'lucide-react';
import {
  getSpecPendingRevisionProposals,
  patchSpecRevisionProposal,
  postSpecRevisionProposalMarkConsumed,
  type SpecRevisionProposal,
} from '@/api/client';

interface Props {
  matterId: string;
  stage: 'stage_3' | 'stage_5';
  /** Optional callback when the drafter clicks "Edit" — host
   * page wires it to the existing claim/figure edit flow. */
  onEditTarget?: (proposal: SpecRevisionProposal) => void;
}

export default function SpecRevisionBanner({
  matterId, stage, onEditTarget,
}: Props): JSX.Element | null {
  const [proposals, setProposals] = useState<SpecRevisionProposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [pendingMutation, setPendingMutation] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await getSpecPendingRevisionProposals(matterId, stage);
      setProposals(rows);
    } catch (e: unknown) {
      const msg =
        (e as { message?: string })?.message ?? 'Failed to load proposals.';
      setToast(msg);
    } finally {
      setLoading(false);
    }
  }, [matterId, stage]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const handleMarkConsumed = async (proposalId: string): Promise<void> => {
    setPendingMutation(proposalId);
    try {
      await postSpecRevisionProposalMarkConsumed(matterId, proposalId);
      setToast('Proposal marked consumed.');
      await reload();
    } catch (e: unknown) {
      setToast(
        (e as { message?: string })?.message ?? 'Mark consumed failed.',
      );
    } finally {
      setPendingMutation(null);
    }
  };

  const handleCancel = async (proposalId: string): Promise<void> => {
    setPendingMutation(proposalId);
    try {
      await patchSpecRevisionProposal(matterId, proposalId, {
        status: 'cancelled',
      });
      setToast('Proposal cancelled.');
      await reload();
    } catch (e: unknown) {
      setToast(
        (e as { message?: string })?.message ?? 'Cancel failed.',
      );
    } finally {
      setPendingMutation(null);
    }
  };

  if (!loading && proposals.length === 0) return null;

  const stageLabel = stage === 'stage_3' ? 'claims' : 'drawings';
  const targetWord = stage === 'stage_3' ? 'Claim' : 'Figure';

  return (
    <div
      className="border-b border-amber-200 bg-amber-50"
      data-testid={`spec-revision-banner-${stage}`}
      role="region"
      aria-label="Spec revision proposals"
    >
      <div className="mx-auto max-w-6xl px-4 py-2">
        <div className="flex items-center gap-2 text-[12px] font-medium text-amber-800">
          <Flag className="h-3.5 w-3.5" />
          {loading
            ? 'Loading spec proposals…'
            : `${proposals.length} proposal${proposals.length === 1 ? '' : 's'} from Specification (${stageLabel})`}
        </div>

        {!loading && proposals.length > 0 && (
          <ul className="mt-2 space-y-2">
            {proposals.map((p) => {
              const isMutating = pendingMutation === p.id;
              const targetId = p.target_kind === 'claim'
                ? p.target_claim_id
                : p.target_drawing_figure_id;
              const sentAt = p.sent_at
                ? new Date(p.sent_at).toLocaleString(undefined, {
                    hour: '2-digit', minute: '2-digit',
                  })
                : '';
              return (
                <li
                  key={p.id}
                  className="rounded-sm border border-amber-200 bg-white px-3 py-2 text-[12px] text-zinc-800"
                  data-testid={`spec-revision-banner-row-${p.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="font-mono text-[10px] text-zinc-500">
                      {targetWord} · {targetId} · proposal sent {sentAt}
                    </span>
                  </div>
                  <p className="mt-1 leading-relaxed">
                    &gt; &ldquo;{p.proposed_change_text}&rdquo;
                  </p>
                  {p.rationale && (
                    <p className="mt-0.5 text-[11px] italic text-zinc-600">
                      Rationale: &ldquo;{p.rationale}&rdquo;
                    </p>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    {onEditTarget && (
                      <button
                        type="button"
                        className="inline-flex h-7 items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 text-[11px] font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
                        onClick={() => onEditTarget(p)}
                        disabled={isMutating}
                        data-testid={`spec-revision-banner-edit-${p.id}`}
                      >
                        Edit {targetWord.toLowerCase()}
                      </button>
                    )}
                    <button
                      type="button"
                      className="inline-flex h-7 items-center gap-1 rounded-sm bg-emerald-600 px-2 text-[11px] font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                      onClick={() => handleMarkConsumed(p.id)}
                      disabled={isMutating}
                      data-testid={`spec-revision-banner-consume-${p.id}`}
                    >
                      {isMutating ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <Check className="h-3 w-3" />
                      )}
                      Mark consumed
                    </button>
                    <button
                      type="button"
                      className="inline-flex h-7 items-center gap-1 rounded-sm border border-zinc-300 bg-white px-2 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50 disabled:opacity-50"
                      onClick={() => handleCancel(p.id)}
                      disabled={isMutating}
                      data-testid={`spec-revision-banner-cancel-${p.id}`}
                    >
                      {isMutating ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        <X className="h-3 w-3" />
                      )}
                      Cancel proposal
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}

        {toast && (
          <p className="mt-2 text-[11px] text-zinc-700">{toast}</p>
        )}
      </div>
    </div>
  );
}
